import type { MouseEvent } from "react";
import { runtimeEnv } from "../lib/runtimeEnv";

export type SupportLinkKind = "discord" | "feedback" | "privacy" | "terms" | "support";

type SupportLinksProps = {
  className?: string;
  discordUrl?: string;
  feedbackUrl?: string;
  includeLegalLinks?: boolean;
  supportEmail?: string;
  onOpenLink?: (kind: SupportLinkKind, url: string) => void;
};

type SupportLink = {
  kind: SupportLinkKind;
  label: string;
  url: string;
};

function buildStaticPageUrl(pathname: string): string {
  const baseUrl = runtimeEnv.BASE_URL ?? "/";
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${normalizedBaseUrl}${pathname.replace(/^\/+/, "")}`;
}

function buildSupportLinks({
  discordUrl,
  feedbackUrl,
  includeLegalLinks = true,
  supportEmail
}: Pick<
  SupportLinksProps,
  "discordUrl" | "feedbackUrl" | "includeLegalLinks" | "supportEmail"
>): SupportLink[] {
  const links: SupportLink[] = [];

  if (discordUrl && discordUrl.trim().length > 0) {
    links.push({
      kind: "discord",
      label: "Discord",
      url: discordUrl
    });
  }

  if (feedbackUrl && feedbackUrl.trim().length > 0) {
    links.push({
      kind: "feedback",
      label: "Feedback",
      url: feedbackUrl
    });
  }

  if (supportEmail && supportEmail.trim().length > 0) {
    links.push({
      kind: "support",
      label: "Support",
      url: supportEmail
    });
  }

  if (includeLegalLinks) {
    links.push(
      {
        kind: "privacy",
        label: "Privacy",
        url: buildStaticPageUrl("privacy.html")
      },
      {
        kind: "terms",
        label: "Terms",
        url: buildStaticPageUrl("terms.html")
      }
    );
  }

  return links;
}

export function SupportLinks({
  className,
  discordUrl,
  feedbackUrl,
  includeLegalLinks = true,
  supportEmail,
  onOpenLink
}: SupportLinksProps) {
  const links = buildSupportLinks({
    discordUrl,
    feedbackUrl,
    includeLegalLinks,
    supportEmail
  });

  const handleClick = (kind: SupportLinkKind, url: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    onOpenLink?.(kind, url);

    if (url.startsWith("mailto:")) {
      return;
    }

    event.currentTarget.rel = "noreferrer";
    event.currentTarget.target = "_blank";
  };

  return (
    <div className={className ? `support-links ${className}` : "support-links"}>
      {links.map((link) => (
        <a
          key={link.kind}
          className="support-link"
          href={link.url}
          onClick={handleClick(link.kind, link.url)}
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}
